import { useState } from 'react';
import { Box, Button, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { CampoCopiavel } from './campo-copiavel';
import { PainelInfo } from './painel-info';
import { URL_TEF_ELGIN, testarConexaoElgin } from '../../lib/tef/elgin';

/**
 * Instruções do TEF ELGIN no editor de forma de pagamento TEF.
 *
 * Simétrico ao painel do webhook do ASAAS, mas o que falta configurar aqui não
 * é um endereço nosso: o ELGIN é um app instalado no Windows do totem, chamado
 * pelo NAVEGADOR do totem em localhost. Nada disso é visível para quem está no
 * backoffice, então a tela precisa dizer o que tem que existir na outra ponta.
 */
export function InstrucoesTefElgin(): JSX.Element {
  const [passosAbertos, setPassosAbertos] = useState(false);
  const [testando, setTestando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; mensagem: string } | null>(null);

  async function testar(): Promise<void> {
    setTestando(true);
    setResultado(null);
    try {
      await testarConexaoElgin();
      setResultado({ ok: true, mensagem: 'O app do TEF respondeu — este computador está pronto para vender.' });
    } catch (err) {
      setResultado({
        ok: false,
        mensagem: `Não foi possível falar com o app do TEF (${
          err instanceof Error ? err.message : 'sem resposta'
        }). Se você não está no computador do totem, isso é esperado.`,
      });
    } finally {
      setTestando(false);
    }
  }

  return (
    <PainelInfo titulo="O TEF roda no computador do totem" denso>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        A maquininha é acionada pelo app da ELGIN instalado no Windows do totem, não pelo nosso servidor. Vincular a
        configuração acima não basta: o app precisa estar instalado e rodando naquele computador.
      </Typography>

      <Stack spacing={1.5}>
        <CampoCopiavel
          rotulo="Endereço usado pelo totem"
          valor={URL_TEF_ELGIN}
          ajuda="Endereço local em que o app da ELGIN precisa estar respondendo, na própria máquina do totem."
        />

        <Box>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={testar}
              disabled={testando}
              startIcon={testando ? <CircularProgress size={16} /> : <PowerSettingsNewIcon />}
            >
              Testar neste computador
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => setPassosAbertos((v) => !v)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 200ms ease',
                    transform: passosAbertos ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
            >
              {passosAbertos ? 'Ocultar checklist' : 'Ver checklist do totem'}
            </Button>
          </Stack>

          {resultado && (
            <Typography
              variant="caption"
              component="p"
              color={resultado.ok ? 'success.main' : 'text.secondary'}
              sx={{ mt: 1 }}
            >
              {resultado.mensagem}
            </Typography>
          )}

          <Collapse in={passosAbertos} timeout={300} unmountOnExit>
            <Box component="ol" sx={{ pl: 2.5, mt: 1, mb: 0, '& li': { mb: 0.75 } }}>
              <li>
                Instale o app de TEF da ELGIN no Windows do totem e deixe-o em execução — é ele que responde no
                endereço acima.
              </li>
              <li>Conecte o pinpad nesse mesmo computador e confirme que ele está habilitado na sua adquirente.</li>
              <li>
                Habilite na adquirente os métodos que você marcou acima (crédito, débito, PIX) — o que não estiver
                habilitado recusa na hora da venda.
              </li>
              <li>
                Deixe marcado apenas o canal <b>Totem</b> nesta forma de pagamento: delivery e mesa/comanda não têm
                pinpad para acionar.
              </li>
              <li>
                Abra o cardápio no próprio totem e faça uma venda de teste. O <b>Testar neste computador</b> acima só
                dá certo se você estiver usando o backoffice na máquina do totem.
              </li>
            </Box>
          </Collapse>
        </Box>
      </Stack>
    </PainelInfo>
  );
}
